import {
    keepPreviousData,
    useQuery,
    type UseQueryResult,
} from '@tanstack/react-query';
import { LIBRARY_DEFAULT_PAGE_SIZE } from 'modules/decision/constants';
import { parseFilterTree } from 'modules/decision/pages/Explore/helpers/filterTree';
import type { ChartPayloadResponseT } from 'modules/decision/types/explore';
import type {
    ChartsTemplatesQueryResponse,
    LibraryIndicatorT,
} from 'modules/decision/types/indicators';
import { RQAPICallWithData } from 'store/APIClient';

type SentenceFilters = {
    metrics?: string[];
    dimensions?: string[];
    filters?: string[];
};

type SuggestedChartResponse = ChartPayloadResponseT<LibraryIndicatorT>;

type SuggestedChartsQueryResponse = Omit<
    ChartsTemplatesQueryResponse,
    'charts'
> & {
    charts: SuggestedChartResponse[];
};

export const useAPIGetSuggestedCharts = (
    sentenceFilters: SentenceFilters,
    page: number,
    keepPrevious = false,
): UseQueryResult<ChartsTemplatesQueryResponse> =>
    useQuery({
        queryKey: ['chartsSuggested', page, sentenceFilters],

        queryFn: async () => {
            const response =
                await RQAPICallWithData<SuggestedChartsQueryResponse>({
                    endpoint: `explore/charts/suggested?page_index=${page}&page_size=${LIBRARY_DEFAULT_PAGE_SIZE}`,
                    method: 'POST',
                    body: sentenceFilters,
                });
            return {
                ...response.content.data,
                charts: response.content.data.charts.map(
                    ({ filterExpression, ...chart }) => ({
                        ...chart,
                        filterTree: parseFilterTree(filterExpression),
                    }),
                ),
            };
        },
        enabled: !!sentenceFilters.metrics?.length,
        // In SingleBanners: keep previous data to avoid a loading flash on change
        // In ViewMore: disable it so pagination doesn't show results from the previous page
        placeholderData: keepPrevious ? keepPreviousData : undefined,
        staleTime: 60_000,
    });
